// Inicialização do Sentry no cliente. Roda no boot do app.
// Complementa o sistema próprio de observabilidade (lib/observability.js +
// systemLogs no Firestore) — não substitui. Sentry serve pra alerta em tempo
// real; o nosso log Firestore é histórico permanente acessível no Admin.

import * as Sentry from '@sentry/react';

// DSN do projeto frontend `mimodocasal-web`. Pode ser exposto no client
// (Sentry trata isso por design — DSN é write-only e a quota é por org).
const DSN = import.meta.env.VITE_SENTRY_DSN || '';

// Detecta ambiente: dev (vite dev), preview/staging, ou produção.
const ENV = (() => {
  if (import.meta.env.DEV) return 'development';
  if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)) return 'development';
  if (typeof window !== 'undefined' && window.location.hostname.includes('staging')) return 'staging';
  return 'production';
})();

let initialized = false;
export function initSentry() {
  if (initialized) return;
  if (!DSN) return; // sem DSN, no-op silencioso (ex.: dev sem .env.local)
  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    // Em dev e localhost, nem mandar — economiza quota.
    enabled: ENV !== 'development',
    // Captura ~10% de transações pra performance — basta pra detectar
    // gargalos sem estourar quota. Sobe se precisar investigar algo.
    tracesSampleRate: 0.1,
    // Replay de sessão só em erro: ajuda demais a debugar UI quebrada.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Filtros: evita ruído de coisas que não conseguimos arrumar.
    ignoreErrors: [
      // Extensões de browser tipicamente
      /chrome-extension/i,
      /moz-extension/i,
      // ResizeObserver loop (benigno, comum em Chrome)
      /ResizeObserver loop/i,
      // Cancelamentos de fetch pelo usuário
      /AbortError/i,
      // Erros de rede que são culpa do convidado (offline, etc.)
      /NetworkError when attempting to fetch resource/i,
      /Failed to fetch/i,
      // Erros de paywall/cookie-blocking de instagram in-app browser
      /Non-Error promise rejection captured/i,
    ],
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      // GTM, Facebook pixel etc se forem adicionados depois
    ],
    // Trims pra não estourar payload
    maxBreadcrumbs: 30,
    normalizeDepth: 5,
  });
}

// Vincula o usuário logado ao erro. Chamar do AuthContext quando o uid muda.
export function setSentryUser(user) {
  if (!initialized) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.uid || '',
    email: user.email || undefined,
  });
}

// Envia erro manualmente — usado pelo reportClientError pra duplicar o sinal
// do nosso log Firestore pro Sentry em paralelo.
export function captureSentryError({ message, stack, context, level = 'error', extra = {} }) {
  if (!initialized) return;
  try {
    const err = new Error(message || 'unknown');
    if (stack) err.stack = stack;
    Sentry.captureException(err, {
      level,
      tags: { context: context || 'manual' },
      extra,
    });
  } catch { /* observabilidade nunca quebra o app */ }
}

export { Sentry };
